import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, StyleSheet, Dimensions } from 'react-native';
import { ActivityIndicator, Text, useTheme } from 'react-native-paper';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../store';
import { updatePerformanceMetrics } from '../../store/slices/dashboardPersonalizationSlice';
import type { DashboardSection } from '../../store/slices/dashboardPersonalizationSlice';

interface LazyDashboardSectionProps {
  section: DashboardSection;
  children: React.ReactNode;
  onLoad?: () => void;
  onError?: (error: Error) => void;
  placeholder?: React.ReactNode;
  threshold?: number; // Pixels from viewport to trigger loading
  retryAttempts?: number;
}

export function LazyDashboardSection({
  section,
  children,
  onLoad,
  onError,
  placeholder,
  threshold = 100,
  retryAttempts = 3,
}: LazyDashboardSectionProps) {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();

  const [isLoaded, setIsLoaded] = useState(section.loadingStrategy === 'eager');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isInViewport, setIsInViewport] = useState(false);

  const sectionRef = useRef<View>(null);
  const loadStartTime = useRef<number>(0);

  // Handle intersection with viewport for lazy loading
  const handleLayout = useCallback(() => {
    if (section.loadingStrategy !== 'lazy' || isLoaded) return;

    sectionRef.current?.measure((x, y, width, height, pageX, pageY) => {
      const screenHeight = Dimensions.get('window').height;
      const isVisible =
        pageY < screenHeight + threshold && pageY + height > -threshold;

      if (isVisible && !isInViewport) {
        setIsInViewport(true);
        loadSection();
      }
    });
  }, [section.loadingStrategy, isLoaded, isInViewport, threshold]);

  // Load section content
  const loadSection = useCallback(async () => {
    if (isLoading || isLoaded) return;

    setIsLoading(true);
    setError(null);
    loadStartTime.current = performance.now();

    try {
      // Simulate async loading (replace with actual loading logic)
      await new Promise(resolve => setTimeout(resolve, 100));

      const loadTime = performance.now() - loadStartTime.current;

      // Track performance metrics
      dispatch(
        updatePerformanceMetrics({
          metric: section.id,
          value: loadTime,
          category: 'loadTime',
        })
      );

      setIsLoaded(true);
      setIsLoading(false);
      onLoad?.();
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to load section');
      setError(error);
      setIsLoading(false);
      onError?.(error);
    }
  }, [isLoading, isLoaded, section.id, dispatch, onLoad, onError]);

  // Retry loading on error
  const retryLoad = useCallback(() => {
    if (retryCount < retryAttempts) {
      setRetryCount(prev => prev + 1);
      setError(null);
      loadSection();
    }
  }, [retryCount, retryAttempts, loadSection]);

  // Handle on-demand loading
  const handleOnDemandLoad = useCallback(() => {
    if (section.loadingStrategy === 'on_demand' && !isLoaded) {
      loadSection();
    }
  }, [section.loadingStrategy, isLoaded, loadSection]);

  // Effect for eager loading
  useEffect(() => {
    if (section.loadingStrategy === 'eager' && !isLoaded) {
      loadSection();
    }
  }, [section.loadingStrategy, isLoaded, loadSection]);

  // Effect for lazy loading viewport detection
  useEffect(() => {
    if (section.loadingStrategy === 'lazy' && !isLoaded) {
      handleLayout();
    }
  }, [section.loadingStrategy, isLoaded, handleLayout]);

  // Render loading state
  if (isLoading) {
    return (
      <View style={[styles.container, styles.loadingContainer]}>
        {placeholder || (
          <View style={styles.defaultPlaceholder}>
            <ActivityIndicator size='small' color={theme.colors.primary} />
            <Text
              variant='bodySmall'
              style={{ color: theme.colors.onSurfaceVariant }}
            >
              Loading {section.title}...
            </Text>
          </View>
        )}
      </View>
    );
  }

  // Render error state
  if (error) {
    return (
      <View style={[styles.container, styles.errorContainer]}>
        <Text variant='bodySmall' style={{ color: theme.colors.error }}>
          Failed to load {section.title}
        </Text>
        {retryCount < retryAttempts && (
          <Text
            variant='bodySmall'
            style={[styles.retryText, { color: theme.colors.primary }]}
            onPress={retryLoad}
          >
            Tap to retry
          </Text>
        )}
      </View>
    );
  }

  // Render on-demand placeholder
  if (section.loadingStrategy === 'on_demand' && !isLoaded) {
    return (
      <View style={[styles.container, styles.onDemandContainer]}>
        <Text
          variant='bodyMedium'
          style={[styles.onDemandText, { color: theme.colors.primary }]}
          onPress={handleOnDemandLoad}
        >
          Tap to load {section.title}
        </Text>
      </View>
    );
  }

  // Render lazy loading placeholder
  if (section.loadingStrategy === 'lazy' && !isLoaded) {
    return (
      <View
        ref={sectionRef}
        style={[styles.container, styles.lazyContainer]}
        onLayout={handleLayout}
      >
        {placeholder || (
          <View style={styles.defaultPlaceholder}>
            <Text
              variant='bodySmall'
              style={{ color: theme.colors.onSurfaceVariant }}
            >
              {section.title}
            </Text>
          </View>
        )}
      </View>
    );
  }

  // Render loaded content
  return (
    <View ref={sectionRef} style={styles.container}>
      {children}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginVertical: 8,
  },
  loadingContainer: {
    minHeight: 80,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    minHeight: 60,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  onDemandContainer: {
    minHeight: 60,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderRadius: 8,
  },
  lazyContainer: {
    minHeight: 80,
    justifyContent: 'center',
    alignItems: 'center',
  },
  defaultPlaceholder: {
    alignItems: 'center',
    gap: 8,
  },
  retryText: {
    marginTop: 8,
    textDecorationLine: 'underline',
  },
  onDemandText: {
    textDecorationLine: 'underline',
  },
});
